'use client';

import type { JSX } from 'react';

import Radius from 'DesignSystem2/Theme/Radius';
import Spacing from 'DesignSystem2/Theme/Spacing';
import Body from 'DesignSystem3/Text/Body';
import Emphasis from 'DesignSystem3/Text/Emphasis';

import { useGraphPayload } from '../interaction/GraphInteractionContext';
import GraphHeaderShell from './GraphHeaderShell';
import type { EventPriceHistoryPayload, MarketTabData } from './payloads';

const MarketTab = ({ title, color, percentage }: MarketTabData) => (
    <div style={{ display: 'flex', gap: Spacing(1), alignItems: 'center' }}>
        <div
            style={{
                minWidth: Spacing(1),
                minHeight: Spacing(1),
                backgroundColor: color,
                borderRadius: Radius.x20,
            }}
        />
        <Body.x20 className="text-text-x20">{title}</Body.x20>
        <div key={percentage} style={{ display: 'flex', alignItems: 'center', height: '100%', minWidth: '45px' }}>
            <Emphasis.x20 className="text-text-x10">{percentage || '\u00A0'}</Emphasis.x20>
        </div>
    </div>
);

const MarketTabRow = ({ tabs }: { tabs: MarketTabData[] }): JSX.Element => (
    <div className="sm:flex-col flex flex-wrap gap-x-1 gap-y-0.5">
        {tabs.map((t, i) => (
            <MarketTab key={'tab' + i} {...t} />
        ))}
    </div>
);

/**
 * Header for the multi-market price-history chart. Composes the `<MarketTab>`
 * row from the body's published `tabs` array; when the body is rendering
 * terminal-name overlays instead, `tabs` is empty and the projection is
 * suppressed.
 */
const EventPriceHistoryHeader = () => {
    const payload = useGraphPayload<EventPriceHistoryPayload>('eventPriceHistory');
    if (!payload) return null;
    if (payload.chartUnavailable || payload.tabs.length === 0) {
        return <GraphHeaderShell />;
    }
    const projection = (
        <div className="flex flex-col">
            {payload.preWrap && payload.tabs.length > 2 ? (
                <div>
                    <MarketTabRow tabs={payload.tabs.slice(0, 2)} />
                    <MarketTabRow tabs={payload.tabs.slice(2)} />
                </div>
            ) : (
                <MarketTabRow tabs={payload.tabs} />
            )}
        </div>
    );
    return <GraphHeaderShell projection={projection} />;
};

export default EventPriceHistoryHeader;
